import { apiFetch } from "../utils/apiFetch";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../context/AuthContext";
import { FiArrowLeft, FiCheck, FiUsers, FiZap, FiAward, FiLoader } from "react-icons/fi";

// const plans = [
//   {
//     id: "basic",
//     name: "Basic",
//     price: 499,
//     description: "Free for 30 days, then ₹499 per month",
//     trial: true,
//     features: [
//       "Create and manage courses",
//       "Live and recorded classes",
//       "Course resources",
//     ],
//   },
//   {
//     id: "plus",
//     name: "Plus",
//     price: 999,
//     description: "₹999 per month",
//     features: [
//       "Everything in Basic",
//       "More storage and learners",
//       "Course analytics",
//     ],
//   },
//   {
//     id: "pro",
//     name: "Pro",
//     price: 1499,
//     description: "₹1,499 per month",
//     features: ["Everything in Plus", "Advanced analytics", "Priority support"],
//   },
// ];

const loadRazorpay = () =>
  new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]',
    );

    if (existingScript) {
      existingScript.addEventListener("load", () => resolve(true));
      existingScript.addEventListener("error", () => resolve(false));
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);

    document.body.appendChild(script);
  });

const ChoosePlan = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [cancellation, setCancellation] = useState(null);
  const [subscriptionEnded, setSubscriptionEnded] = useState(false);
  const { userInfo, userId } = useAuth();
  const currentPlanId = userInfo?.subscriptionInfo?.providerPlanId;
  const subscription = subscriptionDetails || userInfo?.subscriptionInfo;
  const hasEnded = subscriptionEnded || ["cancelled", "completed", "expired"].includes(subscription?.status);
  const cancellationPending = cancellation && ["requested", "confirmed", "unconfirmed"].includes(cancellation.state);
  const canCancel = subscription?.providerSubscriptionId && !subscriptionEnded &&
    !["cancelled", "completed", "expired"].includes(subscription.status);

  useEffect(() => {
    const controller = new AbortController();
    if (!userId) return;
    const loadSubscription = async () => {
      try {
        const response = await apiFetch(`${process.env.API_URL}/api/subscriptions/me`, {
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Could not load subscription");
        if (!controller.signal.aborted) {
          setSubscriptionDetails(data.subscription);
          setCancellation(data.cancellation);
          setSubscriptionEnded(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) setError(error.message);
      }
    };
    loadSubscription();
    return () => controller.abort();
  }, [userId, userInfo?.subscriptionInfo?.providerSubscriptionId, userInfo?.subscriptionInfo?.status]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await apiFetch(`${process.env.API_URL}/api/plans`, {
          headers: {
            "Content-Type": "application/json",

          },
        });

        const plansData = await res.json();

        if (!res.ok) {
          throw new Error(plansData.message || "Could not get plans");
        }

        setPlans(plansData);
      } catch (err) {
        alert(err.message);
      }
    };
    fetchPlans();
  }, []);

  const startCheckout = async (plan) => {
    if (selectedPlan || (canCancel && cancellationPending)) return;

    setSelectedPlan(plan.monthlyPlanId);
    setError("");
    setNotice("");

    try {
      if (!userId) {
        navigate("/login", { replace: true });
        return;
      }

      const endpoint =
        plan.id === "basic"
          ? "/api/subscriptions/basic"
          : "/api/subscriptions/paid";

      const response = await apiFetch(`${process.env.API_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",

        },
        body:
          plan.id === "basic" ? undefined : JSON.stringify({ plan: plan.id }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Could not create subscription");
      }

      if (["plan_upgraded", "plan_change_scheduled", "plan_change_processing"].includes(data.action)) {
        setNotice(data.message);
        setSelectedPlan(null);
        return;
      }

      const razorpayLoaded = await loadRazorpay();
      if (!razorpayLoaded) throw new Error("Could not load Razorpay Checkout");

      const checkout = new window.Razorpay({
        key: data.keyId,
        subscription_id: data.providerSubscriptionId,
        name: "CoachHub",
        description:
          plan.id === "basic" &&
          userInfo?.subscriptionInfo?.status === "default"
            ? "Basic plan: 30 days free, then ₹499/month"
            : `${plan.name} plan: ₹${plan.amount}/month`,

        prefill: {
          name: userInfo?.username,
          email: userInfo?.email,
          contact: "9441101879",
        },

        theme: {
          color: "#111827",
        },
        modal: {
          confirm_close: true,
          escape: false,
          backdropclose: false,
          animation: true,
          ondismiss: () => {
            setSelectedPlan(null);
          },
        },

        handler: (payment) => {
          navigate("/subscription-verification", {
            replace: true,
            state: {
              payment: {
                razorpay_payment_id: payment.razorpay_payment_id,
                razorpay_subscription_id: payment.razorpay_subscription_id,
                razorpay_signature: payment.razorpay_signature,
              },
            },
          });
        },
      });

      checkout.on("payment.failed", (failure) => {
        setError(failure?.error?.description || "Payment authorization failed");
        setSelectedPlan(null);
      });

      checkout.open();

      // checkout.once("ready", (response) => {
      //   setMethods(response.methods);
      // });
    } catch (checkoutError) {
      setError(checkoutError.message);
      setSelectedPlan(null);
    }
  };

  return (
    <main className="min-h-screen bg-[#f6f8f8] text-gray-950">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <button
            type="button"
            onClick={() => navigate("/courses")}
            className="text-lg font-bold"
          >
            CoachHub
          </button>

          <button
            type="button"
            onClick={() => navigate("/courses")}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-950"
          >
            <FiArrowLeft aria-hidden="true" />
            Back to groups
          </button>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 text-sm font-semibold text-orange-700">CoachHub subscriptions</p>
            <h1 className="text-3xl font-bold">Creator plans</h1>
            <p className="mt-2 text-sm text-gray-600">Your groups. Your community.</p>
          </div>
          <span className="text-sm font-medium text-gray-500">Monthly billing</span>
        </div>

        {notice && <p role="status" className="mb-4 text-sm text-orange-700">{notice}</p>}
        {error && (
          <div
            role="alert"
            className="mb-6 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {error}
          </div>
        )}

        <div className="grid gap-5 md:grid-cols-3">
          {!plans ? (
            <div role="status" aria-label="Loading plans" className="col-span-full flex min-h-64 items-center justify-center">
              <FiLoader aria-hidden="true" className="h-7 w-7 animate-spin text-orange-700" />
            </div>
          ) : (
            plans.map((plan) => {
              const loading =
                String(selectedPlan) === String(plan.monthlyPlanId);
              const isCurrentPlan =
                !hasEnded && Boolean(currentPlanId) &&
                String(plan.monthlyPlanId) === String(currentPlanId);
              // const disabled = Boolean(selectedPlan) && !loading;
              const disabled =
                loading || Boolean(selectedPlan) || isCurrentPlan || Boolean(canCancel && cancellationPending);
              const PlanIcon = plan.id === "basic" ? FiUsers : plan.id === "plus" ? FiZap : FiAward;
              const iconStyle = plan.id === "basic" ? "bg-blue-50 text-blue-700" : plan.id === "plus" ? "bg-orange-50 text-orange-700" : "bg-amber-50 text-amber-700";
              const buttonLabel = isCurrentPlan
                ? "Current plan"
                : plan.id === "basic" && userInfo?.subscriptionInfo?.status === "default"
                  ? "Start 30-day trial"
                  : `Choose ${plan.name}`;

              return (
                <article
                  key={plan.id}
                  className={`relative flex min-w-0 flex-col rounded-lg border bg-white p-6 shadow-sm ${
                    plan.id === "plus" ? "border-orange-600 ring-1 ring-orange-600" : "border-gray-200"
                  }`}
                >
                  <div className="mb-6">
                    <div className="mb-5 flex h-10 items-center justify-between gap-3">
                      <span className={`flex h-10 w-10 items-center justify-center rounded-md ${iconStyle}`}>
                        <PlanIcon aria-hidden="true" className="h-5 w-5" />
                      </span>

                      {plan.id === "plus" && (
                        <span className="rounded bg-orange-50 px-2 py-1 text-xs font-semibold text-orange-800">
                          Popular
                        </span>
                      )}
                    </div>
                    <h2 className="text-xl font-bold">{plan.name}</h2>

                    <div className="mt-5 flex items-end gap-1">
                      <span className="text-3xl font-bold">
                        ₹{(plan.amount / 100).toLocaleString("en-IN")}
                      </span>
                      <span className="pb-1 text-sm text-gray-500">/month</span>
                    </div>

                    <p className="mt-3 min-h-10 text-sm text-gray-600">
                      {plan.description}
                    </p>
                  </div>

                  <ul className="mb-8 flex-1 space-y-3 border-t border-gray-100 pt-6 text-sm text-gray-700">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <FiCheck
                          aria-hidden="true"
                          className="mt-0.5 h-4 w-4 shrink-0 text-orange-700"
                        />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    disabled={disabled}
                    aria-busy={loading}
                    aria-label={loading ? `Processing ${plan.name}` : buttonLabel}
                    onClick={() => startCheckout(plan)}
                    className={`flex h-11 w-full items-center justify-center rounded-md px-4 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:cursor-not-allowed ${
                      isCurrentPlan
                        ? "border border-gray-300 bg-white text-gray-800"
                        : plan.id === "plus"
                          ? "bg-orange-700 text-white enabled:hover:bg-orange-800"
                          : "bg-gray-950 text-white enabled:hover:bg-gray-800"
                    }`}
                  >
                    {loading ? <FiLoader aria-hidden="true" className="h-5 w-5 animate-spin" /> : buttonLabel}
                  </button>

                  {plan.trial && (
                    <p className="mt-3 text-center text-xs text-gray-500">
                      ₹499/month after 30 days. Cancel anytime.
                    </p>
                  )}
                </article>
              );
            })
          )}
        </div>
        {(canCancel || cancellation || hasEnded) && (
          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-gray-200 pt-6">
            <div className="min-w-0 text-sm text-gray-600" aria-live="polite">
              <h2 className="font-semibold text-gray-950">Subscription renewal</h2>
              {hasEnded ? <p className="mt-1">Subscription ended.</p> : cancellationPending ? (
                <p className="mt-1">{cancellation.state !== "confirmed"
                  ? "Cancellation confirmation is pending. Contact support before retrying."
                  : cancellation.atCycleEnd && cancellation.effectiveAt
                    ? `Renewal cancelled. Current access ends ${new Date(cancellation.effectiveAt).toLocaleString()}.`
                    : "Subscription cancelled."}</p>
              ) : <p className="mt-1">Cancel renewal without losing the remainder of your paid billing cycle.</p>}
            </div>
          </div>
        )}
      </section>
    </main>
  );
};

export default ChoosePlan;
